import DeleteModal from "components/DeleteModal";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useState } from "react";
import { deletePowerOfAgency } from "services/complaintDetailService";
import { ComplaintReception, PowerOfAgency } from "types";
import { errorHandler } from "utils/helper";
import PowerOfAgencyDetail from "../model/PowerOfAgencyDetail";
import PowerOfAgencyForm from "../model/PowerOfAgencyForm";

type Props = {
  compliant: ComplaintReception;
  setRefetch: any;
  canUpdate: boolean;
};

const PowerOfAgencyTab: React.FC<Props> = ({
  compliant,
  setRefetch,
  canUpdate,
}) => {
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();

  const [action, setAction] = useState<"Add" | "Edit">("Add");
  const [isOpenForm, setIsOpenForm] = React.useState(false);
  const [isOpenDetail, setIsOpenDetail] = React.useState(false);
  const [isOpenDelete, setIsOpenDelete] = React.useState(false);
  const [selectedData, setSelectedData] = React.useState<PowerOfAgency>();

  const handleOpenForm = () => {
    setIsOpenForm(true);
  };

  const handleCloseForm = (refetch = false) => {
    setIsOpenForm(false);
    if (refetch) setRefetch();
  };

  const handleOpenDetail = () => {
    setIsOpenDetail(true);
  };

  const handleCloseDetail = (refetch = false) => {
    setIsOpenDetail(false);
    if (refetch) {
      setRefetch();
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">
            Power of Agency
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Authorized representatives and legal agents
          </p>
        </div>
        {canUpdate && (
          <button
            onClick={() => {
              setAction("Add");
              handleOpenForm();
            }}
            className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all flex items-center shadow-sm hover:shadow"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
              />
            </svg>
            Add Representative
          </button>
        )}
      </div>

      {compliant?.power_of_agency_info_detail &&
      compliant?.power_of_agency_info_detail.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-xl">
          <div className="w-16 h-16 mx-auto mb-4 bg-purple-50 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-purple-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <h4 className="text-lg font-medium text-gray-700 mb-2">
            No Representatives Added
          </h4>
          <p className="text-gray-500 mb-4">
            Add authorized representatives for the complaint
          </p>
          {canUpdate && (
            <button
              onClick={() => {
                setAction("Add");
                handleOpenForm();
              }}
              className="px-4 py-2 text-purple-600 hover:text-purple-800 font-medium"
            >
              Add your first representative →
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {compliant?.power_of_agency_info_detail?.map(
            (powerOfAgency, index) => (
              <div
                key={index}
                className="group border border-gray-200 hover:border-purple-300 rounded-xl p-4 transition-all hover:shadow-md bg-white"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-sm">
                            {powerOfAgency?.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="text-lg font-semibold text-gray-900">
                            {powerOfAgency?.name}
                          </h4>
                          <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                            Representative
                          </span>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center text-sm text-gray-600">
                            <svg
                              className="w-4 h-4 mr-2 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                              />
                            </svg>
                            {powerOfAgency?.phone_number || "Not provided"}
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <svg
                              className="w-4 h-4 mr-2 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                              />
                            </svg>
                            {powerOfAgency?.email || "Not provided"}
                          </div>
                          <div className="text-sm text-gray-500">
                            {[
                              powerOfAgency?.region,
                              powerOfAgency?.zone_or_city,
                              powerOfAgency?.woreda_or_kebele,
                              powerOfAgency?.sub_city,
                              powerOfAgency?.kebele,
                              powerOfAgency?.house_number,
                            ]
                              .filter(Boolean)
                              .join(", ")}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedData({ ...powerOfAgency });
                        handleOpenDetail();
                      }}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </button>
                    {canUpdate && (
                      <>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setAction("Edit");
                            setSelectedData(powerOfAgency);
                            handleOpenForm();
                          }}
                          className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedData(powerOfAgency);
                            setIsOpenDelete(true);
                          }}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ),
          )}
        </div>
      )}

      {isOpenForm && (
        <PowerOfAgencyForm
          action={action}
          complaintId={compliant?.id || ""}
          powerOfAgency={action === "Edit" ? selectedData : undefined}
          isOpen={isOpenForm}
          onClose={handleCloseForm}
        />
      )}

      <PowerOfAgencyDetail
        powerOfAgency={selectedData}
        isOpen={isOpenDetail}
        onClose={handleCloseDetail}
      />

      {selectedData && (
        <DeleteModal
          isOpen={isOpenDelete}
          onConfirm={() => {
            deletePowerOfAgency(selectedData)
              .then(() => {
                showTooltip("Representative successfully deleted!", "success");
                setRefetch();
              })
              .catch((error) => {
                showTooltip(
                  error?.response?.data?.detail || errorHandler(error),
                );
              });
          }}
          onCancel={() => {
            setIsOpenDelete(false);
          }}
          title="Delete Representative"
          message={`Are you sure you want to delete ${selectedData?.name}? This action cannot be undone.`}
        />
      )}
    </div>
  );
};

export default PowerOfAgencyTab;
